/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import ApiService from '@services/api';
import { useSession } from '@hooks/useSession';
import IDownload from '@interfaces/IDownload';

import Loading from '@components/Loading';
import Modal from '@components/Modal';
import NotAuthenticated from '@components/NotAuthenticated';
import Button from '@components/Button';
import Paginator from '@components/Paginator';
import { useParams } from 'react-router-dom';
import New, { INewDownload } from './New';
import DownloadRow from './Row';

import { Container, Title, WrapperTable, Table } from './styles';

const Download: React.FC = () => {
  const params = useParams();
  const { isMigrating } = useSession();
  const { api } = new ApiService(params.token);

  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [newDownload, setNewDownload] = useState<INewDownload | null>();
  const [records, setRecords] = useState<IDownload[]>([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(100);
  const [queryCount, setQueryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isMigrating) {
      loadRecords();
    }
  }, [page, isMigrating]);

  async function loadRecords() {
    try {
      setLoading(true);

      const queryParams: any = {
        page,
      };

      const resp = await api.get('/downloads', {
        params: queryParams,
      });
      setRecords(resp.data.items);
      setTotalRecords(resp.data.total);
      setQueryCount(resp.data.queryCount);
      setAuthenticated(true);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setAuthenticated(false);
      } else {
        toast.error('Falha ao carregar');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(download: IDownload) {
    try {
      window.open(download.url);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('Download indisponível');
      } else {
        toast.error('Falha ao realizar o download');
      }
    }
  }

  async function handleNewDownload(download: INewDownload | null) {
    try {
      setNewDownload(download);

      await api.post('/downloads/xml', download);
      toast.success('Download solicitado com sucesso!');
      setNewDownload(null);
      loadRecords();
      queryClient.invalidateQueries({ queryKey: ['downloadStatus'] });
    } catch (error: any) {
      toast.error(
        `Falha ao solicitar download. ${error?.response?.data?.error || ''}`
      );
    }
  }

  if (loading)
    return (
      <Container>
        <Loading />
      </Container>
    );

  if (!authenticated) {
    return <NotAuthenticated />;
  }

  return (
    <Container>
      <Title>DOWNLOAD DE ARQUIVOS XML</Title>
      <Button onClick={() => setNewDownload({} as INewDownload)}>
        Novo Download
      </Button>
      <p style={{ marginTop: 20 }}>
        *Os downloads solicitados vão para uma fila de processamento e podem
        levar alguns minutos para ficarem disponíveis. Após concluído, o arquivo
        ficará disponível para download por 30 dias.
      </p>
      {records.length > 0 && (
        <WrapperTable>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Data/Hora de Solicitação</th>
                <th style={{ width: '50%' }}>Descrição</th>
                <th>Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <DownloadRow
                  key={record.id}
                  initialRecord={record}
                  api={api}
                  handleDownload={handleDownload}
                  setError={setErrorMessage}
                />
              ))}
            </tbody>
          </Table>
          <div>
            Exibindo {page > 1 ? (page - 1) * 20 + 1 : 1} a{' '}
            {page > 1 ? (page - 1) * 20 + queryCount : queryCount} de um total
            de {totalRecords} registros.
          </div>
        </WrapperTable>
      )}

      {totalRecords > 20 && (
        <Paginator
          activePage={Number(page)}
          limitPage={20}
          totalRecords={totalRecords}
          onChange={(newPage) => setPage(newPage)}
        />
      )}

      {newDownload && (
        <New
          onConfirm={(inutilizacao) => handleNewDownload(inutilizacao)}
          onCancel={() => setNewDownload(null)}
        />
      )}

      {errorMessage && (
        <Modal onCloseRequest={() => setErrorMessage('')} exitButton={false}>
          <div style={{ padding: 40 }}>{errorMessage}</div>
        </Modal>
      )}
    </Container>
  );
};

export default Download;
